<template>
    <Head title="Ticket Expenditure & Cost Report" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <div>
                <Link :href="route('reports.tickets.index')" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2 inline-flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Ticket Expenditure & Cost Report</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Analyze costs incurred on tickets including labor, materials, and external repairs
                </p>
            </div>
        </div>

        <!-- Filters Section -->
        <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Filters</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <Input
                    v-model="filters.date_from"
                    type="date"
                    label="Date From"
                    :error="dateError && !filters.date_from ? 'Start date is required' : ''"
                    required
                    :max="filters.date_to"
                />
                <Input
                    v-model="filters.date_to"
                    type="date"
                    label="Date To"
                    :error="dateError && !filters.date_to ? 'End date is required' : ''"
                    required
                    :min="filters.date_from"
                />
                <div class="flex items-end">
                    <p class="text-xs text-gray-600 dark:text-gray-400 pb-3">
                        <span class="font-medium text-amber-600 dark:text-amber-400">Note:</span> Date range is required and cannot exceed 365 days
                    </p>
                </div>
            </div>
            <div :class="subscriptionFeatures.equipment_module ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                    <Select v-model="filters.status" :options="statusOptions" multiple placeholder="All Statuses" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                    <Select v-model="filters.category" :options="categoryOptions" multiple placeholder="All Categories" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                    <Select v-model="filters.location" :options="locationOptions" multiple placeholder="All Locations" />
                </div>
                <div v-if="subscriptionFeatures.equipment_module">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Equipment</label>
                    <Select v-model="filters.equipment" :options="equipmentOptions" multiple placeholder="All Equipment" />
                </div>
            </div>
        </div>

        <!-- Actions Toolbar -->
        <div class="flex items-center justify-between">
            <Button @click="generateReport" :disabled="loading">
                <span v-if="!loading">Generate Report</span>
                <span v-else class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                </span>
            </Button>

            <div v-if="reportData.length > 0 && canExport" class="flex gap-2">
                <Button variant="secondary" @click="exportPdf">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export PDF
                </Button>
                <Button variant="secondary" @click="exportCsv">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                </Button>
            </div>
        </div>

        <!-- Report Results -->
        <Card v-if="reportData.length > 0">
            <div class="p-6">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Report Results</h2>
                    <span class="text-sm text-gray-600 dark:text-gray-400">{{ reportData.length }} tickets</span>
                </div>

                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ticket #</th>
                                <th v-if="subscriptionFeatures.equipment_module" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Equipment</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date Created</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date Resolved</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reporter</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignee</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Labor Cost</th>
                                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Material Cost</th>
                                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">External Cost</th>
                                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Actual</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="(row, index) in reportData" :key="index">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ row.ticket_number }}</td>
                                <td v-if="subscriptionFeatures.equipment_module" class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.equipment }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.location }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.category }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.date_created }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.date_resolved }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.reporter }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ row.assignee }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span :class="getStatusClass(row.status)">{{ row.status }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">{{ row.labor_cost }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">{{ row.material_cost }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">{{ row.external_cost }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">{{ row.total_actual_cost }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Card>

        <!-- Empty State -->
        <Card v-else-if="!loading">
            <div class="p-12 text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="text-sm text-gray-500 dark:text-gray-400">Select filters and click "Generate Report" to view data</p>
            </div>
        </Card>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Select from '@/Components/Common/Select.vue';
import Input from '@/Components/Common/Input.vue';
import route from '@/Utils/route';
import { useToast } from '@/Composables/useToast';
import { useSecureAxios } from '@/Composables/useSecureAxios';

const toast = useToast();
const axios = useSecureAxios();
const page = usePage();

// Subscription features
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });

const props = defineProps({
    filterOptions: Object,
    canExport: Boolean,
});

// Helper function to get current month dates
const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();

    return {
        start: `${year}-${month}-01`,
        end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`
    };
};

// State
const loading = ref(false);
const reportData = ref([]);
const dateError = ref('');
const filters = ref({
    status: [],
    location: [],
    equipment: [],
    category: [],
    date_from: '',
    date_to: '',
});

// Initialize with current month dates
onMounted(() => {
    const currentMonth = getCurrentMonthDates();
    filters.value.date_from = currentMonth.start;
    filters.value.date_to = currentMonth.end;
});

// Computed
const statusOptions = computed(() => {
    return props.filterOptions.statuses.map(s => ({
        value: s,
        label: s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }));
});

const locationOptions = computed(() => {
    return props.filterOptions.locations.map(l => ({ value: l, label: l }));
});

const equipmentOptions = computed(() => {
    return props.filterOptions.equipment.map(e => ({ value: e.id, label: e.name }));
});

const categoryOptions = computed(() => {
    return props.filterOptions.categories.map(c => ({ value: c.id, label: c.name }));
});

// Date range validation
const validateDateRange = () => {
    dateError.value = '';

    // Check if both dates are filled
    if (!filters.value.date_from || !filters.value.date_to) {
        dateError.value = 'Both start and end dates are required';
        return false;
    }

    const fromDate = new Date(filters.value.date_from);
    const toDate = new Date(filters.value.date_to);

    // Check if end date is after start date
    if (toDate < fromDate) {
        dateError.value = 'End date must be after start date';
        return false;
    }

    // Check if range is within 365 days
    const diffTime = Math.abs(toDate - fromDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 365) {
        dateError.value = 'Date range cannot exceed 365 days (1 year)';
        return false;
    }

    return true;
};

// Methods
const generateReport = async () => {
    // Validate date range before generating
    if (!validateDateRange()) {
        toast.error(dateError.value);
        return;
    }

    loading.value = true;

    try {
        const response = await axios.post(route('reports.tickets.expenditure.generate'), filters.value);

        if (response.data.success) {
            reportData.value = response.data.data;
            toast.success(`Generated ${response.data.total} ticket expenditure records`);
        }
    } catch (error) {
        toast.error('Failed to generate report');
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const exportPdf = async () => {
    try {
        const response = await axios.post(route('reports.tickets.export-pdf'), {
            report_type: 'expenditure',
            data: reportData.value,
            filters: filters.value,
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ticket-expenditure-report-${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('PDF exported successfully');
    } catch (error) {
        toast.error('Failed to export PDF');
        console.error(error);
    }
};

const exportCsv = async () => {
    try {
        const response = await axios.post(route('reports.tickets.export-csv'), {
            report_type: 'expenditure',
            data: reportData.value,
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ticket-expenditure-report-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('CSV exported successfully');
    } catch (error) {
        toast.error('Failed to export CSV');
        console.error(error);
    }
};

const getStatusClass = (status) => {
    const statusLower = status.toLowerCase();
    const classes = {
        'open': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        'assigned': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        'scheduled': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
        'in progress': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        'on hold': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        'pending': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        'resolved': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        'closed': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
        'cancelled': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        're opened': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    };

    return classes[statusLower] || 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};
</script>
